import { useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../ui/ToastSystem';
import { supabase } from '../../lib/supabase';

// ─────────────────────────────────────────
// PlanChangeNotifier
//
// Watches profile.subscription_tier and reacts to upgrades, downgrades
// or cancellations by:
//   1. Deleting all rows the user owns in ai_cache, any AI-generated
//      analysis will be regenerated on the next access with the new
//      plan's configuration.
//   2. Showing a toast: "Seu plano foi atualizado. Suas análises serão
//      regeneradas com sua nova configuração."
//
// What is NOT deleted:
//   • profile data (name, birth date, birth chart computation)
//   • reading history or user-generated content (dream journal, etc.)
//   • localStorage forecast cache (holds public sign forecasts, not
//     personalized analysis)
//
// The component renders null, it exists purely for the side-effect.
// Mount once near the root of the authenticated tree.
// ─────────────────────────────────────────

export function PlanChangeNotifier() {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const previousTier = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Signed out, reset baseline so the next login doesn't fire a
    // spurious "plan changed" event.
    if (!profile) {
      previousTier.current = undefined;
      return;
    }

    const currentTier = profile.subscription_tier ?? 'free';

    // First observation for this profile, just capture the baseline.
    if (previousTier.current === undefined) {
      previousTier.current = currentTier;
      return;
    }

    // No change → nothing to do.
    if (previousTier.current === currentTier) return;

    // Plan actually changed, update baseline, then clean up.
    const fromTier = previousTier.current;
    previousTier.current = currentTier;

    // Fire-and-forget: clear the user's ai_cache entries.
    // If RLS blocks this for any reason the entries will expire
    // naturally via the ai_cache.expires_at TTL.
    supabase
      .from('ai_cache')
      .delete()
      .eq('user_id', profile.id)
      .then(({ error }) => {
        if (error) {
          console.warn('[PlanChangeNotifier] ai_cache clear failed', error);
        }
      });

    showToast({
      type: 'info',
      message: 'Seu plano foi atualizado. Suas análises serão regeneradas com sua nova configuração.',
    });

    // Suppress unused-var warning in release builds while keeping the
    // value available for a future audit-log integration.
    void fromTier;
  }, [profile, showToast]);

  return null;
}
